import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { addUnsavedChange, clearUnsavedChanges, removeUnsavedChangeById, SaveEntityType, selectUnsavedState, setShowUnSaveModelInfo, UnsavedChange } from "../app/slice/unsavedSlice";
import { selectAppState } from "../app/slice/appSlice";
import { useToast } from "@reconvert/react-ui-component";
import { useTranslation } from "react-i18next";
import { selectConditionsState } from "../app/slice/conditionSlice";
import { IFramePostMessageType, sendPostMessage } from "../utils/helpers";

export const PreventActionTypeObject = {
  CONDITION_CREATE_UPDATE: "CONDITION_CREATE_UPDATE",
  INLINE_CHANGE: "INLINE_CHANGE"
}

export type PreventActionType = "CONDITION_CREATE_UPDATE" | "INLINE_CHANGE";

interface UnsavedBarReturnType {
  handleSave: () => Promise<boolean>;
  handleDiscard: () => void;
  preventAction: (callback: any, type?: PreventActionType) => void;
  pushUnsavedChanges: ({
    id,
    type,
    saveAction,
    discardAction,
  }: {
    id: string;
    type: SaveEntityType;
    saveAction: () => void;
    discardAction: () => void;
  }) => Promise<void>;
  unsavedChanges: UnsavedChange[];
  removeUnsavedChange: (id: string) => void;
}

const OrderOfSaveEntityType: Record<SaveEntityType, number> = {
  WIDGET: 1,
  VARIANT: 2,
  "SLOT-CONFIG": 3,
  SLOT: 4,
  CONDITION: 5,
};

const useUnsavedBar = (): UnsavedBarReturnType => {
  const { t } = useTranslation();
  const { isUnsavedBarEnabled } = useAppSelector(selectAppState);
  const { selectedSlot, selectedConditionId, conditions } = useAppSelector(selectConditionsState);
  const { success, error } = useToast();
  const { unsavedChanges, isUnsavedBarVisible, isConditionCreateOrUpdate } = useAppSelector(selectUnsavedState);
  const dispatch = useAppDispatch();

  const variants = useMemo(() => {
    if (!selectedSlot?.isABTestingEnabled) return []

    return conditions.find(a => a.id === selectedConditionId)?.variants?.filter((v) => v.slotId === selectedSlot?.id)
  }, [conditions, selectedConditionId, selectedSlot?.id, selectedSlot?.isABTestingEnabled])

  const handleSave = useCallback(async () => {
    if (variants && variants?.length > 0) {
      const totalImpressionShare = variants?.reduce((a, b) => a + b.abImpressionShare, 0);

      if (totalImpressionShare !== 100) {
        sendPostMessage(IFramePostMessageType.SHOW_SAVE_BAR, {
          isInlineChange: true,
        });

        error(
          t("The total impression share across all variants must equal 100%. Please adjust the values accordingly."),
          {
            duration: 5000,
          },
        );

        return false;
      }
    }

    const sortedPromise = unsavedChanges.slice().sort((a, b) => a.order - b.order);
    const allUnsavedPromise = sortedPromise.map(a => a.promise());

    const res = await Promise.allSettled(allUnsavedPromise);

    dispatch(clearUnsavedChanges());

    if (res.every(a => a.status === "fulfilled")) {
      success(t("Changes saved successfully."));
      return true;
    }

    return false;
  }, [dispatch, success, t, unsavedChanges, variants]);

  const handleDiscard = useCallback(async () => {
    const sortedPromise = unsavedChanges.slice().sort((a, b) => a.order - b.order);
    const allUnsavedPromiseForDiscard = sortedPromise.map(a => a.fallback && a.fallback());

    await Promise.allSettled(allUnsavedPromiseForDiscard);

    dispatch(clearUnsavedChanges());
  }, [dispatch, unsavedChanges]);

  const pushUnsavedChanges = useCallback(
    async ({
      id,
      type,
      saveAction,
      discardAction,
    }: {
      id: string;
      type: SaveEntityType;
      saveAction: () => void;
      discardAction: () => void;
    }) => {
      //Only Push to Unsaved Promise when isUnsavedBarEnabled set to TRUE
      if (isUnsavedBarEnabled) {
        dispatch(
          addUnsavedChange({
            id,
            order: OrderOfSaveEntityType[type],
            promise: () =>
              new Promise((resolve, reject) => {
                try {
                  saveAction();
                  resolve(`${id} save successfully!!`);
                } catch (error) {
                  reject(`${id} save rejected!!`);
                }
              }),
            fallback: () =>
              new Promise((resolve, reject) => {
                try {
                  discardAction();
                  resolve(`${id} discard successfully!!`);
                } catch (error) {
                  reject(`${id} discard rejected!!`);
                }
              }),
          }),
        );
      } else {
        saveAction();
      }
    },
    [dispatch, isUnsavedBarEnabled],
  );


  const preventAction = useCallback(
    (callback: any, type?: "CONDITION_CREATE_UPDATE" | "INLINE_CHANGE") => {
      if (isUnsavedBarEnabled && isUnsavedBarVisible) {
        if (!type) {
          dispatch(setShowUnSaveModelInfo({ callback }))
          return;
        }

        if (isConditionCreateOrUpdate && type === "INLINE_CHANGE") {
          dispatch(setShowUnSaveModelInfo({ callback }))
          return;
        }
      }

      callback();
    },
    [isConditionCreateOrUpdate, isUnsavedBarVisible, isUnsavedBarEnabled, dispatch],
  );

  const removeUnsavedChange = useCallback((id: string) => {
    if (!isUnsavedBarEnabled || !isUnsavedBarVisible || !unsavedChanges.some(a => a.id === id)) return;

    dispatch(removeUnsavedChangeById({ id }))
  }, [dispatch, isUnsavedBarEnabled, isUnsavedBarVisible, unsavedChanges])

  return {
    unsavedChanges,
    handleSave,
    handleDiscard,
    pushUnsavedChanges,
    preventAction,
    removeUnsavedChange
  };
};

export default useUnsavedBar;
